import { AxiosResponse, AxiosInstance } from 'axios';
import some from 'lodash/some';

import { GETTERS as CUSTOMER_GETTERS, MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { GETTERS as RESTAURANT_GETTERS } from '@/store/restaurant';

import {
  RecentOrFavoriteOrder,
  RecentOrFavoriteEntree,
  RecentOrFavoriteItem,
  RecentOrFavoriteEntreeContents,
  MenuMetadataForChannel,
  CustomerProfile,
  CachedRecentOrFavoriteOrders,
} from '@/models';
import { ApiService } from '../api/api.service';
import { isCustomerCanadian, isMenuItemRewardsExclusive, PromiseCacher } from '@/helpers';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { AnalyticsService } from '../analytics/analytics.service';

export interface RecentAndFavoriteOrdersService {
  refreshFavoriteOrders(numRecords?: number): Promise<void>;
  refreshRecentOrders(numRecords?: number): Promise<void>;
  clearFavoriteOrdersCache(): void;
  clearRecentOrdersCache(): void;

  addRecentOrderToFavorites(order: RecentOrFavoriteOrder, name: string): Promise<void>;
  addOrderToFavorites(orderId: string, name: string): Promise<void>;

  removeOrderFromFavorites(orderId: string): Promise<void>;
  orderHasExclusiveItems(order: RecentOrFavoriteOrder): boolean;
  isEligibleForReorder(order: RecentOrFavoriteOrder, isEnrolledInLoyalty: boolean): boolean;
}

export class AppRecentAndFavoriteOrdersService extends ApiService implements RecentAndFavoriteOrdersService {
  get orderCustomerBaseRoute(): string {
    return '/order/v3/customer';
  }  

  get currentCountryCode(): string {
    return this.store.state.ui.countryProfile.countryCode;
  }

  get menuMetadata(): MenuMetadataForChannel | null {
    return this.store.state.menu.metadata;
  }

  get customer(): CustomerProfile | null {
    return this.store.state.customer.profile;
  }

  get isCustomerCanadian(): boolean | null {
    return isCustomerCanadian(this.customer, this.store.state);
  }

  get favoriteOrders(): RecentOrFavoriteOrder[] {
    return this.store.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];
  }

  get favoriteOrdersExpiration(): Date | null {
    return this.store.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS_EXPIRATION];
  }

  get recentOrders(): CachedRecentOrFavoriteOrders {
    return this.store.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get recentOrdersExpiration(): Date | null {
    return this.store.getters[CUSTOMER_GETTERS.RECENT_ORDERS_EXPIRATION];
  }

  get selectedRestaurantId(): number {
    return this.store.getters[RESTAURANT_GETTERS.GET_SELECTED_RESTAURANT_ID];
  }

  get hasJwt(): boolean {
    return this.store.getters[CUSTOMER_GETTERS.HAS_JWT];
  }

  cacheExpirationMinutes = 10;

  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, private promiseCacher: PromiseCacher,  protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  async refreshFavoriteOrders(numRecords?: number) {
    if (!this.store.state.customer.profile || !this.hasJwt) {
      this.logger.debug('Attempted to refresh favorite orders without a customer set');
      return;
    }

    if (!this.selectedRestaurantId) {
      // TODO-8jpndq: figure out what to do
      this.logger.warn('No restaurant selected -- skipping refresh');
      return;
    }

    if (!this.shouldRefreshCacheKey(this.favoriteOrdersExpiration)) {
      return;
    }

    const restaurantId = this.selectedRestaurantId;
    const favoriteOrders = await this.promiseCacher.exec<RecentOrFavoriteOrder[]>(`refreshFavoriteOrders:${restaurantId}`, true, async () => {
      return await this.httpClient
        .get(`${this.orderCustomerBaseRoute}/favorite`, {
          params: {
            restaurantId,
            numRecords,
          },
        })
        .then((r: AxiosResponse<RecentOrFavoriteOrder[]>) => r.data)
        .catch(e => this.logAndThrowApiError('refreshFavoriteOrders', e));
    });
    const cacheEntry: CachedRecentOrFavoriteOrders = {
      expiration: this.getCacheExpirationDate(false, this.favoriteOrdersExpiration),
      orders: favoriteOrders ?? [],
    };
    this.setFavoriteOrdersCache(cacheEntry);
  }

  async refreshRecentOrders(numRecords?: number) {
    if (!this.store.state.customer.profile || !this.hasJwt) {
      this.logger.debug('Attempted to refresh recent orders without a customer set');
      return;
    }

    if (!this.selectedRestaurantId) {
      // TODO-8jpndq: figure out what to do
      this.logger.warn('No restaurant selected -- skipping refresh');
      return;
    }
    if (!this.shouldRefreshCacheKey(this.recentOrdersExpiration)) {
      return;
    }

    const restaurantId = this.selectedRestaurantId;
    const recentOrders = await this.promiseCacher.exec(`refreshRecentOrders:${restaurantId}`, true, async () => {
      return await this.httpClient
        .get(`${this.orderCustomerBaseRoute}/recent`, {
          params: {
            restaurantId,
            numRecords,
          },
        })
        .then((r: AxiosResponse<RecentOrFavoriteOrder[]>) => r.data)
        .catch(e => this.logAndThrowApiError('refreshRecentOrders', e));
    });
    const recentsCacheEntry = {
      expiration: this.getCacheExpirationDate(false, this.recentOrdersExpiration),
      orders: recentOrders ?? [],
    };
    this.setRecentOrdersCache(recentsCacheEntry);
  }

  async addRecentOrderToFavorites(order: RecentOrFavoriteOrder, name: string): Promise<void> {
    const addOrderPromise = this.addOrderToFavorites(order.orderId, name);

    const originalFavorites = [...this.favoriteOrders];
    const originalExpiration = this.favoriteOrdersExpiration ? new Date(this.favoriteOrdersExpiration) : this.getCacheExpirationDate(true, this.favoriteOrdersExpiration);

    const expirationDate = this.getCacheExpirationDate(true, this.favoriteOrdersExpiration);

    const newFavorites = originalFavorites.concat([order]);
    this.setFavoriteOrdersCache({ expiration: expirationDate, orders: newFavorites });

    try {
      await addOrderPromise;
    } catch (e: any) {
      // In case of an error, revert to the previous favorites
      this.setFavoriteOrdersCache({ expiration: originalExpiration, orders: originalFavorites });
    }
  }

  async addOrderToFavorites(orderId: string, name: string): Promise<void> {
    const request = {
      OrderId: orderId,
      Favoritename: name,
    };

    await this.httpClient
      .post(`${this.orderCustomerBaseRoute}/favorite`, request)
      .then(r => r.data)
      .catch(e => this.logAndThrowApiError('addOrderToFavorites', e));
  }

  async removeOrderFromFavorites(orderId: string): Promise<void> {
    const removeOrderPromise = this.httpClient
      .delete(`${this.orderCustomerBaseRoute}/favorite/${orderId}`)
      .then(r => r.data)
      .catch(e => this.logAndThrowApiError('removeOrderFromFavorites', e));

    const originalFavorites = [...this.favoriteOrders];
    const originalExpiration = new Date(this.favoriteOrdersExpiration as Date);
    const newFavoriteOrders = originalFavorites.filter(o => o.orderId != orderId);

    // Check if favorites is different than newFavorites
    if (newFavoriteOrders.length == originalFavorites.length) {
      this.logger.warn(`Tried to remove an order from local favorites but couldn't find the order`, { orderId });

      return;
    }

    const newExpiration = this.getCacheExpirationDate(true, this.favoriteOrdersExpiration); // expire the cache on next access
    this.setFavoriteOrdersCache({ expiration: newExpiration, orders: newFavoriteOrders });

    try {
      await removeOrderPromise;
    } catch (e: any) {
      // In case of an error, revert to the previous favorites
      this.setFavoriteOrdersCache({ expiration: originalExpiration, orders: originalFavorites });
    }
  }

  clearFavoriteOrdersCache() {
    this.store.commit(CUSTOMER_MUTATIONS.SET_FAVORITE_ORDERS, {});
  }

  clearRecentOrdersCache() {
    this.store.commit(CUSTOMER_MUTATIONS.SET_RECENT_ORDERS, {});
  }

  isEligibleForReorder(order: RecentOrFavoriteOrder, isEnrolledInLoyalty: boolean): boolean {
    return (this.isCustomerCanadian || isEnrolledInLoyalty || !this.orderHasExclusiveItems(order)) && order.order.isOrderAvailable;
  }

  orderHasExclusiveItems(order: RecentOrFavoriteOrder): boolean {
    return some(order.order.meals, meal => this.hasExclusiveItems(meal.entrees) || this.hasExclusiveItems(meal.sides) || this.hasExclusiveItems(meal.drinks));
  }

  private getCacheExpirationDate(shouldInvalidate: boolean, originalExpiration: Date | null) {
    const now = new Date();
    if (shouldInvalidate) {
      now.setMilliseconds(0);
      return now;
    }

    if (this.shouldRefreshCacheKey(originalExpiration)) {
      const date = new Date();
      date.setMinutes(date.getMinutes() + this.cacheExpirationMinutes);
      date.setMilliseconds(0);
      return date;
    }

    return originalExpiration as Date;
  }

  private shouldRefreshCacheKey(expiration: Date | null) {
    const now = new Date();

    if (expiration) {
      // if expiration is in the past refresh the cache
      return now >= expiration;
    }

    // no cached items--refresh the cache
    return true;
  }

  // Sides and drinks do not have nested contents.
  // Entrees may have nested contents. Hence the recursive call.
  private hasExclusiveItems(items: RecentOrFavoriteItem[] | RecentOrFavoriteEntreeContents[] | RecentOrFavoriteEntree[] | null): boolean {
    return some(items, (item: RecentOrFavoriteEntree) => {
      const itemMetadata = this.menuMetadata && this.menuMetadata.items[item.menuItemId];
      return (itemMetadata && isMenuItemRewardsExclusive(itemMetadata.tags)) || (item.contents && this.hasExclusiveItems(item.contents));
    });
  }

  private setRecentOrdersCache(recents: CachedRecentOrFavoriteOrders) {
    const newRecentsCache = { ...this.store.state.customer.recentOrders, ...{ [this.selectedRestaurantId]: recents } };
    this.store.commit(CUSTOMER_MUTATIONS.SET_RECENT_ORDERS, newRecentsCache);
  }

  private setFavoriteOrdersCache(favorites: CachedRecentOrFavoriteOrders) {
    const newFavoritesCache = { ...this.store.state.customer.favoriteOrders, ...{ [this.selectedRestaurantId]: favorites } };
    this.store.commit(CUSTOMER_MUTATIONS.SET_FAVORITE_ORDERS, newFavoritesCache);
  }
}
